import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StickyNote, Download, Loader2, Check, Lock } from "lucide-react";
import { toast } from "sonner";

/** Per-lesson notes card shown beneath the lesson content for students.
 *
 * - Auto-save with 800 ms debounce.
 * - Locks automatically once the chapter is completed (backend returns 423).
 * - Download button exports just this lesson's notes as Markdown.
 * - Private to the student — instructors/admins never see this component.
 */
export default function LessonNotesCard({ lesson, userRole, variant }) {
    const [content, setContent] = useState("");
    const [locked, setLocked] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | loading | saving | saved | error
    const [loaded, setLoaded] = useState(false);
    const saveTimer = useRef(null);

    // Only surface notes UI to students (instructors/admins should never see
    // another user's notes, and don't have notes of their own on this path).
    const enabled = userRole === "student" && !!lesson?.id;

    useEffect(() => {
        if (!enabled) return;
        let live = true;
        setStatus("loading");
        (async () => {
            try {
                const { data } = await api.get(`/lessons/${lesson.id}/my-note`);
                if (!live) return;
                setContent(data.content || "");
                setLocked(!!data.locked);
                setStatus(data.content ? "saved" : "idle");
            } catch {
                if (live) setStatus("error");
            } finally {
                if (live) setLoaded(true);
            }
        })();
        return () => {
            live = false;
        };
    }, [enabled, lesson?.id]);

    useEffect(() => {
        if (!enabled || !loaded || locked) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        setStatus("saving");
        saveTimer.current = setTimeout(async () => {
            try {
                await api.put(`/lessons/${lesson.id}/my-note`, { content });
                setStatus("saved");
            } catch (err) {
                if (err?.response?.status === 423) {
                    setLocked(true);
                    toast.info("Chapter complete — your notes are now locked.");
                } else {
                    setStatus("error");
                }
            }
        }, 800);
        return () => {
            if (saveTimer.current) clearTimeout(saveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);

    const download = async (format = "md") => {
        try {
            const resp = await api.get(
                `/lessons/${lesson.id}/my-note/download?format=${format}`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(lesson.title || "lesson").replace(/[^A-Za-z0-9._ -]/g, "")} notes.${format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download the notes.");
        }
    };

    if (!enabled) return null;

    // Inline variant — host provides its own surface chrome (used by the new
    // Learn.jsx apricot notes card). Renders just the status row + textarea +
    // footer, no outer border/background.
    if (variant === "inline") {
        return (
            <div data-testid="lesson-notes-card">
                <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                    <span>Visible only to you. Auto-saved as you type.</span>
                    <div className="flex-1" />
                    {status === "saving" && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    )}
                    {status === "saved" && !locked && (
                        <span className="inline-flex items-center gap-1">
                            <Check className="h-3 w-3 text-primary" /> Saved
                        </span>
                    )}
                    {locked && (
                        <span className="inline-flex items-center gap-1">
                            <Lock className="h-3 w-3" /> Locked
                        </span>
                    )}
                </div>
                <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    readOnly={locked}
                    placeholder={
                        locked
                            ? "This chapter is complete. Your notes are locked as a record."
                            : "What did you learn? What's confusing? Write anything here — it's yours."
                    }
                    className="w-full min-h-[140px] resize-y rounded-xl border px-4 py-3 text-sm leading-relaxed outline-none bg-white transition-all focus:ring-4 focus:ring-accent/20 focus:border-accent"
                    style={{ borderColor: "rgba(212, 161, 64, 0.25)" }}
                    data-testid="lesson-notes-textarea"
                />
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <span className="text-[11px] text-muted-foreground">
                        Markdown supported: <code className="px-1.5 py-0.5 bg-black/5 rounded">## heading</code>, <code className="px-1.5 py-0.5 bg-black/5 rounded">- bullet</code>, <code className="px-1.5 py-0.5 bg-black/5 rounded">**bold**</code>
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => download("md")}
                        className="gap-1.5 text-primary/70 hover:text-primary hover:bg-white"
                        disabled={!content}
                        data-testid="lesson-notes-download"
                    >
                        <Download className="h-3.5 w-3.5" />
                        Download lesson notes
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="mt-6 rounded-lg border border-border bg-card p-5"
            data-testid="lesson-notes-card"
        >
            <div className="flex items-center gap-2 mb-3">
                <StickyNote className="h-4 w-4 text-accent" />
                <h3 className="font-display text-base text-foreground">My notes</h3>
                <span className="text-[11px] text-muted-foreground">
                    (visible only to you)
                </span>
                <div className="flex-1" />
                {status === "saving" && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                )}
                {status === "saved" && !locked && (
                    <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                        <Check className="h-3 w-3 text-primary" /> Saved
                    </span>
                )}
                {locked && (
                    <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                        <Lock className="h-3 w-3" /> Locked
                    </span>
                )}
            </div>
            <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                readOnly={locked}
                placeholder={
                    locked
                        ? "This chapter is complete. Your notes are locked as a record."
                        : "What did you learn? What's confusing? Write anything here — it's yours."
                }
                className="w-full min-h-[160px] resize-y rounded-md border border-border bg-background px-3 py-2 text-sm font-mono leading-relaxed outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="lesson-notes-textarea"
            />
            <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">
                    Markdown supported: <code>## heading</code>, <code>- bullet</code>,{" "}
                    <code>**bold**</code>
                </span>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => download("md")}
                    className="gap-1.5"
                    disabled={!content}
                    data-testid="lesson-notes-download"
                >
                    <Download className="h-3.5 w-3.5" />
                    Download
                </Button>
            </div>
        </div>
    );
}
